import { Injectable } from '@angular/core';
import {
  TALK_TO_YOUR_DATA_HISTORY_STORAGE_KEY,
  TALK_TO_YOUR_DATA_SAVED_CONVERSATIONS_STORAGE_KEY,
} from '../../constants/chatbot.constants';
import { ConversationSummary } from '../../domain/models/conversation-summary.model';

interface StoredConversationSummary {
  readonly conversationId: string;
  readonly previewText: string;
  readonly lastActivityDate: string;
  readonly title?: string;
}

@Injectable()
export class ConversationHistoryStorageAdapter {
  loadHistory(): readonly ConversationSummary[] {
    return this.loadList(TALK_TO_YOUR_DATA_HISTORY_STORAGE_KEY);
  }

  saveHistory(history: readonly ConversationSummary[]): void {
    this.saveList(TALK_TO_YOUR_DATA_HISTORY_STORAGE_KEY, history);
  }

  loadSavedConversations(): readonly ConversationSummary[] {
    return this.loadList(TALK_TO_YOUR_DATA_SAVED_CONVERSATIONS_STORAGE_KEY);
  }

  saveSavedConversations(savedConversations: readonly ConversationSummary[]): void {
    this.saveList(TALK_TO_YOUR_DATA_SAVED_CONVERSATIONS_STORAGE_KEY, savedConversations);
  }

  private loadList(storageKey: string): readonly ConversationSummary[] {
    const storage = this.getStorage();

    if (!storage) {
      return [];
    }

    const rawList = storage.getItem(storageKey);

    if (!rawList) {
      return [];
    }

    try {
      const parsedList: unknown = JSON.parse(rawList);

      if (!Array.isArray(parsedList)) {
        return [];
      }

      return parsedList
        .map((item: unknown) => this.mapStoredItem(item))
        .filter((item: ConversationSummary | null): item is ConversationSummary => item !== null);
    } catch {
      return [];
    }
  }

  private saveList(storageKey: string, list: readonly ConversationSummary[]): void {
    const storage = this.getStorage();

    if (!storage) {
      return;
    }

    const serializableList = list
      .map((conversationSummary: ConversationSummary) => this.toStoredConversationSummary(conversationSummary))
      .filter(
        (conversationSummary: StoredConversationSummary | null): conversationSummary is StoredConversationSummary =>
          conversationSummary !== null,
      );

    storage.setItem(storageKey, JSON.stringify(serializableList));
  }

  clearHistory(): void {
    const storage = this.getStorage();

    if (!storage) {
      return;
    }

    storage.removeItem(TALK_TO_YOUR_DATA_HISTORY_STORAGE_KEY);
  }

  private getStorage(): Storage | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage;
  }

  private mapStoredItem(item: unknown): ConversationSummary | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const typedItem = item as Partial<StoredConversationSummary>;

    if (
      typeof typedItem.conversationId !== 'string' ||
      typeof typedItem.previewText !== 'string' ||
      typeof typedItem.lastActivityDate !== 'string'
    ) {
      return null;
    }

    if (typedItem.title !== undefined && typeof typedItem.title !== 'string') {
      return null;
    }

    return {
      conversationId: typedItem.conversationId,
      id: typedItem.conversationId,
      previewText: typedItem.previewText,
      lastActivityDate: typedItem.lastActivityDate,
      title: typedItem.title,
    };
  }

  private toStoredConversationSummary(summary: ConversationSummary): StoredConversationSummary | null {
    const conversationId = this.resolveConversationId(summary);

    if (!conversationId) {
      return null;
    }

    return {
      conversationId,
      previewText: summary.previewText,
      lastActivityDate: summary.lastActivityDate,
      ...(summary.title ? { title: summary.title } : {}),
    };
  }

  private resolveConversationId(summary: ConversationSummary): string | null {
    if (summary.conversationId) {
      return summary.conversationId;
    }

    if (summary.id) {
      return summary.id;
    }

    return null;
  }
}
